import { useEffect, useState } from "react";
import { createSubject, deleteSubject, listSubjects } from "../lib/api";

const NAVY = "#0b1f4a";
const ACCENT = "#b68235";
const ACCENT_100 = "#fff3e4";
const ACCENT_800 = "#5a3b0a";
const MAX_MEMBERS = 4;

type Member = {
  id: string;
  name: string;
  meta: string;
};

// Tidigare låg två påhittade medlemmar här (Anna och Erik Exempel) som visades
// för alla, och addMember sparade bara i lokalt state — en sidladdning
// nollställde listan. Nu ligger personerna i D1 och gränsen kontrolleras av
// servern, inte av MAX_MEMBERS här nedan.

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2);
}

/**
 * Familj — 2 of 4 members, add button, upgrade CTA.
 * Mock only. Route suggestion: /family or /familj
 */
export default function Family() {
  const [members, setMembers] = useState<Member[]>([]);
  const [activeId, setActiveId] = useState("");
  const [newName, setNewName] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [max, setMax] = useState(MAX_MEMBERS);
  const [busy, setBusy] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const load = async () => {
    try {
      const res = await listSubjects();
      setMembers(
        (res.data ?? []).map((s) => ({
          id: s.id,
          name: s.fullName,
          meta: s.city ? `${s.city}` : "På kontot",
        })),
      );
      setMax(res.meta?.max ?? MAX_MEMBERS);
      setActiveId((prev) => prev || res.data?.[0]?.id || "");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      showToast(msg === "unauthorized" ? "Logga in för att se familjen" : "Kunde inte hämta");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const slotsUsed = members.length;
  const canAdd = slotsUsed < max;

  const addMember = async () => {
    const name = newName.trim();
    if (!name) {
      showToast("Ange ett namn");
      return;
    }
    setBusy(true);
    try {
      await createSubject({ fullName: name });
      setNewName("");
      await load();
      showToast(`${name} tillagd`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      showToast(
        msg === "subject_limit_reached"
          ? max >= 4
            ? "Familj rymmer fyra personer"
            : "Din plan täcker en person — Familj rymmer fyra"
          : msg === "unauthorized"
            ? "Du är utloggad"
            : "Kunde inte lägga till",
      );
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (id: string, name: string) => {
    setBusy(true);
    try {
      await deleteSubject(id);
      await load();
      showToast(`${name} borttagen`);
    } catch {
      showToast("Kunde inte ta bort");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-page="family" style={{ fontFamily: '"Lora", Georgia, serif', color: "#201f1d" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
        <h1
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 400,
            fontSize: 34,
            margin: 0,
            color: NAVY,
            flex: 1,
          }}
        >
          Familj
        </h1>
        <span style={{ fontSize: 12.5, color: "#9b9797", fontFeatureSettings: '"tnum" 1' }}>
          {slotsUsed} av {max} platser använda
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          marginTop: 22,
          maxWidth: 640,
        }}
      >
        {members.map((s) => {
          const isActive = s.id === activeId;
          return (
            <div
              key={s.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: "#fff",
                border: `1px solid ${isActive ? ACCENT : "color-mix(in srgb, #201f1d 16%, transparent)"}`,
                borderRadius: 7,
                padding: "16px 20px",
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: ACCENT_100,
                  color: ACCENT_800,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {initials(s.name)}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: NAVY }}>{s.name}</div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: "#9b9797",
                    fontFeatureSettings: '"tnum" 1',
                  }}
                >
                  {s.meta}
                </div>
              </div>
              {isActive ? (
                <span
                  style={{
                    fontSize: 10.5,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    padding: "3px 10px",
                    borderRadius: 99,
                    background: ACCENT_100,
                    color: ACCENT_800,
                  }}
                >
                  Aktiv
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setActiveId(s.id);
                    showToast(`Bytte till ${s.name}`);
                  }}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
                    borderRadius: 4,
                    background: "transparent",
                    fontSize: 13,
                    color: "#605d5d",
                    cursor: "pointer",
                  }}
                >
                  Byt till
                </button>
              )}
              <button
                type="button"
                onClick={() => void removeMember(s.id, s.name)}
                disabled={busy}
                aria-label={`Ta bort ${s.name}`}
                title="Tar bort personen och alla ärenden som hör till"
                style={{
                  padding: "8px 12px",
                  border: 0,
                  background: "transparent",
                  fontSize: 13,
                  color: "#B42318",
                  cursor: busy ? "default" : "pointer",
                }}
              >
                Ta bort
              </button>
            </div>
          );
        })}

        <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
          <input
            placeholder="Namn på ny person"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void addMember();
            }}
            disabled={!canAdd}
            style={{
              flex: 1,
              minWidth: 180,
              padding: "11px 14px",
              border: "1px dashed #d7d3d3",
              borderRadius: 4,
              fontSize: 14,
              background: "transparent",
              color: "#201f1d",
              fontFamily: "inherit",
            }}
          />
          <button
            type="button"
            onClick={() => void addMember()}
            disabled={!canAdd}
            style={{
              padding: "11px 20px",
              border: `1px solid ${NAVY}`,
              borderRadius: 4,
              background: "transparent",
              fontSize: 14,
              color: NAVY,
              cursor: canAdd ? "pointer" : "not-allowed",
              opacity: canAdd ? 1 : 0.5,
            }}
          >
            + Lägg till person
          </button>
        </div>

        {/* Upgrade CTA */}
        <div
          style={{
            marginTop: 8,
            background: "#fff",
            border: `1px solid ${ACCENT}`,
            borderRadius: 7,
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
          data-testid="family-upgrade-cta"
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: NAVY }}>
              Behöver ni fler än {MAX_MEMBERS}?
            </div>
            <div style={{ fontSize: 13, color: "#7d7979", marginTop: 4, lineHeight: 1.55 }}>
              Familjeplanen rymmer upp till 4 personer. Organisation ger fler seats, central admin
              och faktura.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              window.location.href = "mailto:info@linespotting.com?subject=GDPR%20Nordic%20Organisation";
            }}
            style={{
              padding: "11px 20px",
              border: `1px solid ${NAVY}`,
              borderRadius: 4,
              background: "transparent",
              fontSize: 14,
              color: NAVY,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Uppgradera →
          </button>
        </div>

        <p style={{ fontSize: 12.5, color: "#9b9797", margin: "4px 0 0" }}>
          Familjeplanen rymmer upp till 4 personer. Behöver ni fler?{" "}
          <a href="https://gdpr-nordic.org/priser" style={{ color: "#1a3a7a" }}>
            Kontakta oss om Organisation
          </a>
          .
        </p>
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            background: NAVY,
            color: "#fff",
            borderRadius: 4,
            padding: "12px 22px",
            fontSize: 14,
            zIndex: 80,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
